import { Controller, ForbiddenException, Get, Query, Req, UseGuards } from '@nestjs/common';
import { ApiBearerAuth, ApiOperation, ApiSecurity, ApiTags } from '@nestjs/swagger';
import { AdminAccessGuard } from '../auth/admin-access.guard';
import { AuthGuard, AuthenticatedRequest } from '../auth/auth.guard';
import {
  MerchantAuditQuery,
  MerchantAuditService,
  MerchantAuditViewer,
} from './merchant-audit.service';

/** Roles that may open store activity at all. */
const CAN_VIEW = new Set([
  'store_owner', 'merchant', 'store_manager', 'accountant',
  // Platform staff supporting a merchant see what the merchant sees.
  'admin', 'super_admin', 'platform_admin', 'account_manager', 'operator',
]);

/**
 * Roles that see the whole store's history.
 *
 * `store_manager` is deliberately NOT here: they are among the people the log
 * records, so their default is their own actions only. An owner who wants a
 * manager to review the whole team's history can grant it explicitly — that is
 * a decision worth making on purpose rather than inheriting from a role name.
 */
const CAN_VIEW_EVERYONE = new Set([
  'store_owner', 'merchant',
  'admin', 'super_admin', 'platform_admin', 'account_manager', 'operator',
]);

@ApiTags('Merchant · Activity')
@ApiBearerAuth()
@ApiSecurity('store-id')
@Controller('merchant/audit')
@UseGuards(AuthGuard, AdminAccessGuard)
export class MerchantAuditController {
  constructor(private readonly merchantAudit: MerchantAuditService) {}

  /**
   * AdminAccessGuard has already proven this user is active staff of the store
   * in context, so cross-tenant access is settled before this runs. What is
   * decided here is narrower: whether they may read the log at all, and whether
   * they see the whole team or only themselves.
   */
  private viewer(request: AuthenticatedRequest): MerchantAuditViewer {
    const user = request.user;
    if (!user || !CAN_VIEW.has(user.role)) {
      throw new ForbiddenException('You do not have access to store activity.');
    }

    return { userId: user.id, seeEveryone: CAN_VIEW_EVERYONE.has(user.role) };
  }

  @Get('activity')
  @ApiOperation({ summary: 'Store activity, grouped by the action that caused it' })
  activity(@Query() query: MerchantAuditQuery, @Req() request: AuthenticatedRequest) {
    return this.merchantAudit.listActivity(query, this.viewer(request));
  }

  @Get('history')
  @ApiOperation({ summary: 'Everything that ever happened to one product or order' })
  history(@Query() query: MerchantAuditQuery, @Req() request: AuthenticatedRequest) {
    return this.merchantAudit.recordHistory(query, this.viewer(request));
  }

  @Get('actors')
  @ApiOperation({ summary: 'Team members who appear in this store’s history' })
  actors(@Req() request: AuthenticatedRequest) {
    const viewer = this.viewer(request);
    if (!viewer.seeEveryone) return [];

    return this.merchantAudit.actors();
  }

  @Get('summary')
  summary(@Req() request: AuthenticatedRequest) {
    this.viewer(request);
    return this.merchantAudit.summary();
  }
}
